// Repair a known Tripo auto-rig defect: arm weights bleeding into the head.
//
// On some builds (seen on a shoulder-length bob whose ends touch a high collar)
// Tripo's rig gives head/hair vertices 10-50% of their weight on the upper-arm
// twist bones. The bind pose is perfect, but as soon as the arms move (any clip,
// even the static rest pose's arm drop) those vertices are dragged towards the
// shoulders and the hair/face tear into spikes. The rig check on the server only
// looks at each vertex's dominant joint, so these builds pass it.
//
// Fix: for every vertex whose dominant joint is the head or neck, drop its
// weights on arm-chain joints (upper arm, forearm, hand, their twist bones) and
// renormalise onto what is left. Clavicle weights are kept: collars and hair
// ends resting on the shoulders legitimately follow them. Mutates the geometry
// in place (it is shared by every clone of a cached glTF), once per geometry.
import type { Object3D, SkinnedMesh } from 'three';

const HEAD_OWNER = /^(Head|NeckTwist0[12])$/;
const ARM_CHAIN = /^[LR]_(Upperarm|Forearm|Hand)/;

/** Returns how many vertices were repaired (0 when nothing needed it or already done). */
export function repairHeadArmWeights(root: Object3D): number {
  let fixed = 0;
  root.traverse((o) => {
    const mesh = o as SkinnedMesh;
    if (!mesh.isSkinnedMesh || !mesh.skeleton) return;
    const g = mesh.geometry;
    if (g.userData.headArmWeightsRepaired) return;
    g.userData.headArmWeightsRepaired = true;
    const bones = mesh.skeleton.bones;
    if (!bones.some((b) => b.name === 'L_Upperarm')) return; // Tripo spec only
    const si = g.getAttribute('skinIndex'), sw = g.getAttribute('skinWeight');
    if (!si || !sw) return;
    const head = bones.map((b) => HEAD_OWNER.test(b.name));
    const arm = bones.map((b) => ARM_CHAIN.test(b.name));
    let changed = false;
    for (let i = 0; i < sw.count; i++) {
      let dom = -1, dw = -1;
      for (let k = 0; k < 4; k++) { const w = sw.getComponent(i, k); if (w > dw) { dw = w; dom = si.getComponent(i, k); } }
      if (!head[dom]) continue;
      let removed = 0, kept = 0;
      for (let k = 0; k < 4; k++) {
        const w = sw.getComponent(i, k);
        if (w > 0 && arm[si.getComponent(i, k)]) removed += w; else kept += w;
      }
      if (removed <= 0 || kept <= 0) continue;
      for (let k = 0; k < 4; k++) {
        const w = sw.getComponent(i, k);
        sw.setComponent(i, k, arm[si.getComponent(i, k)] ? 0 : w / kept);
      }
      fixed++; changed = true;
    }
    if (changed) sw.needsUpdate = true;
  });
  return fixed;
}
